import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import FileUpload from "@/components/marketplace/FileUpload";
import StatusPill from "@/components/marketplace/StatusPill";
import { FREELANCER_STATUS } from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";

export default function FreelancerApply() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    full_name: "", country: "", timezone: "", languages: "", years_experience: "",
    experience: "", avg_delivery_days: "", max_workload: "", hours: "", description: "",
    software: "",
  });
  const [allCats, setAllCats] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);
  const [portfolioTitle, setPortfolioTitle] = useState("");
  const [portfolioImages, setPortfolioImages] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/register?next=/freelancer/apply"); return; }
    if (user.role !== "freelancer") { nav("/"); return; }
    api.get("/freelancer/me").then((r) => {
      setProfile(r.data);
      if (r.data?.personal) {
        setForm((f) => ({
          ...f,
          full_name: r.data.personal.full_name || user.name,
          country: r.data.personal.country || "",
          timezone: r.data.personal.timezone || "",
          languages: (r.data.personal.languages || []).join(", "),
          years_experience: r.data.personal.years_experience || "",
          experience: r.data.professional?.experience || "",
          avg_delivery_days: r.data.professional?.avg_delivery_days || "",
          max_workload: r.data.professional?.max_workload || "",
          hours: r.data.professional?.hours || "",
          description: r.data.professional?.description || "",
          software: (r.data.software || []).join(", "),
        }));
        setPortfolio(r.data.portfolio || []);
        setSelectedCats(r.data.categories || []);
      }
    });
    // Real category list so designers pick from actual names instead of free
    // text — free-text ("rings") never matched job categories ("Ring") and
    // silently emptied the available-jobs pool.
    api.get("/categories").then((r) => setAllCats(r.data || [])).catch(() => {});
  }, [user, loading, nav]);

  // Case/plural-insensitive check so legacy free-text values still show as selected.
  const normCat = (s) => { const t = (s || "").trim().toLowerCase(); return t.endsWith("s") ? t.slice(0, -1) : t; };
  const isCatSelected = (name) => selectedCats.some((c) => normCat(c) === normCat(name));
  const toggleCat = (name) => setSelectedCats((prev) =>
    prev.some((c) => normCat(c) === normCat(name)) ? prev.filter((c) => normCat(c) !== normCat(name)) : [...prev, name]
  );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPortfolio = () => {
    if (!portfolioTitle) return toast.error("Add a title");
    setPortfolio([...portfolio, { title: portfolioTitle, images: portfolioImages.map((f) => f.id), image_names: portfolioImages.map((f) => f.name) }]);
    setPortfolioTitle(""); setPortfolioImages([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!accept) return toast.error("Please accept the confidentiality and non-circumvention terms.");
    setBusy(true);
    try {
      await api.post("/freelancer/apply", {
        personal: {
          full_name: form.full_name, country: form.country, timezone: form.timezone,
          languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
          years_experience: Number(form.years_experience) || 0,
        },
        professional: {
          experience: form.experience,
          avg_delivery_days: Number(form.avg_delivery_days) || 0,
          max_workload: Number(form.max_workload) || 1,
          hours: form.hours,
          description: form.description,
        },
        portfolio,
        software: form.software.split(",").map((s) => s.trim()).filter(Boolean),
        categories: selectedCats,
        accept_terms: true,
      });
      toast.success("Application submitted — you'll hear from us soon.");
      nav("/freelancer");
    } catch (ex) { toast.error(apiErr(ex)); } finally { setBusy(false); }
  };

  // noindex: this page is a login-gated form, useless to a crawler, because a
  // logged-out visitor is bounced to /register before any of it renders, so
  // all a search engine could index is a spinner.
  //
  // It is declared here, ABOVE the `profile` guard, and rendered on both
  // branches on purpose. Placing it only in the main return below would emit
  // the noindex solely for a signed-in designer whose profile had already
  // loaded, i.e. for everyone except the crawlers it is meant for.
  //
  // robots.txt keeps `Allow: /freelancer/apply` as an exception to
  // `Disallow: /freelancer` so this tag stays reachable. A blocked page can
  // never be read, so its noindex can never be obeyed; a URL that is linked
  // site-wide from the Nav and Footer but blocked from crawling is exactly the
  // case that gets indexed as a bare, description-less result.
  const seo = <Seo title="Apply as a Designer" path="/freelancer/apply" noindex />;

  if (!profile) {
    return (
      <>
        {seo}
        <div className="p-20 text-center text-muted-foreground">Loading…</div>
      </>
    );
  }

  const locked = ["approved", "suspended", "rejected"].includes(profile.status);

  return (
    <div className="max-w-[1000px] mx-auto px-4 md:px-8 py-10 md:py-16 space-y-10">
      {seo}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Designer Application</div>
          <h1 className="font-serif text-4xl md:text-5xl">Join the GetJewelDesigns studio.</h1>
        </div>
        <StatusPill testid="freelancer-status" status={profile.status} map={FREELANCER_STATUS} />
      </div>

      {locked && (
        <div className="border hairline p-6 text-sm text-muted-foreground">
          {profile.status === "approved" && "Your application is approved. You now have access to available projects."}
          {profile.status === "rejected" && `Your application was not accepted. ${profile.rejection_reason || ""}`}
          {profile.status === "suspended" && "Your account has been suspended. Please contact support."}
        </div>
      )}

      <form onSubmit={submit} className={`space-y-10 ${locked ? "opacity-60 pointer-events-none" : ""}`} data-testid="apply-form">
        <section>
          <h2 className="font-serif text-3xl mb-6">Personal</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div><Label className="text-xs uppercase tracking-widest">Full name</Label><Input data-testid="apply-name" value={form.full_name} onChange={set("full_name")} className="mt-2 bg-transparent border hairline rounded-none" required /></div>
            <div><Label className="text-xs uppercase tracking-widest">Country</Label><Input data-testid="apply-country" value={form.country} onChange={set("country")} className="mt-2 bg-transparent border hairline rounded-none" /></div>
            <div><Label className="text-xs uppercase tracking-widest">Timezone</Label><Input data-testid="apply-tz" value={form.timezone} onChange={set("timezone")} className="mt-2 bg-transparent border hairline rounded-none" /></div>
            <div><Label className="text-xs uppercase tracking-widest">Languages (comma-separated)</Label><Input data-testid="apply-langs" value={form.languages} onChange={set("languages")} className="mt-2 bg-transparent border hairline rounded-none" /></div>
            <div><Label className="text-xs uppercase tracking-widest">Years experience</Label><Input data-testid="apply-years" type="number" value={form.years_experience} onChange={set("years_experience")} className="mt-2 bg-transparent border hairline rounded-none" /></div>
          </div>
        </section>

        <section>
          <h2 className="font-serif text-3xl mb-6">Professional</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest">CAD experience</Label><Textarea data-testid="apply-exp" value={form.experience} onChange={set("experience")} className="mt-2 bg-transparent border hairline rounded-none min-h-[100px]" /></div>
            <div><Label className="text-xs uppercase tracking-widest">Software (comma-separated)</Label><Input data-testid="apply-sw" value={form.software} onChange={set("software")} placeholder="Rhino, Matrix, ZBrush" className="mt-2 bg-transparent border hairline rounded-none" /></div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Categories</Label>
              {allCats.length === 0 ? (
                <div className="mt-2 text-sm text-muted-foreground">Loading categories…</div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="apply-cats">
                  {allCats.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`apply-cat-${c.slug}`}
                      onClick={() => toggleCat(c.name)}
                      aria-pressed={isCatSelected(c.name)}
                      className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors duration-300 ${
                        isCatSelected(c.name) ? "border-primary bg-primary text-primary-foreground" : "hairline hover:border-primary/60"
                      }`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">Pick every category you can design for. Leave all unselected to receive projects from every category.</p>
            </div>
            <div><Label className="text-xs uppercase tracking-widest">Avg delivery (days)</Label><Input data-testid="apply-delivery" type="number" value={form.avg_delivery_days} onChange={set("avg_delivery_days")} className="mt-2 bg-transparent border hairline rounded-none" /></div>
            <div><Label className="text-xs uppercase tracking-widest">Max concurrent projects</Label><Input data-testid="apply-workload" type="number" value={form.max_workload} onChange={set("max_workload")} className="mt-2 bg-transparent border hairline rounded-none" /></div>
            <div><Label className="text-xs uppercase tracking-widest">Working hours</Label><Input data-testid="apply-hours" value={form.hours} onChange={set("hours")} placeholder="10-18 IST" className="mt-2 bg-transparent border hairline rounded-none" /></div>
            <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest">Short professional description</Label><Textarea data-testid="apply-desc" value={form.description} onChange={set("description")} className="mt-2 bg-transparent border hairline rounded-none min-h-[100px]" /></div>
          </div>
        </section>

        <section>
          <h2 className="font-serif text-3xl mb-6">Portfolio</h2>
          <div className="border hairline p-6 space-y-4">
            <div><Label className="text-xs uppercase tracking-widest">Item title</Label><Input data-testid="portfolio-title" value={portfolioTitle} onChange={(e) => setPortfolioTitle(e.target.value)} className="mt-2 bg-transparent border hairline rounded-none" /></div>
            <div><Label className="text-xs uppercase tracking-widest">Images / files</Label><div className="mt-2"><FileUpload value={portfolioImages} onChange={setPortfolioImages} category="portfolio" max={5} testid="portfolio-files" /></div></div>
            <Button type="button" data-testid="portfolio-add" onClick={addPortfolio} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs">Add to portfolio</Button>
          </div>
          {portfolio.length > 0 && (
            <ul className="mt-4 space-y-2">
              {portfolio.map((p, i) => (
                <li key={i} className="border hairline p-3 text-sm flex justify-between">
                  <span>{p.title}</span>
                  <span className="text-muted-foreground text-xs">{(p.image_names || p.images || []).length} file(s)</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <label className="flex items-start gap-3 text-sm text-muted-foreground">
          <Checkbox data-testid="apply-accept" checked={accept} onCheckedChange={setAccept} className="rounded-none border-primary/50 mt-1" />
          <span>I accept the platform's confidentiality and non-circumvention terms. I will not attempt to identify or contact clients outside the platform.</span>
        </label>

        <Button data-testid="apply-submit" type="submit" disabled={busy} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs py-6 px-10">
          {busy ? "Submitting…" : "Submit application"}
        </Button>
      </form>
    </div>
  );
}
